import { useEffect, useState } from 'react'
import { CheckCircle, XCircle } from 'lucide-react'
import Modal from './Modal'
import { normalizeImageKitUrl } from '@lic/media-urls'

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

type UploadState = 'idle' | 'uploading' | 'success' | 'error'

interface UploadPreviewModalProps {
  file: File
  onConfirm: (opts: { filename: string; alt: string }) => void
  onClose: () => void
  uploadState?: UploadState
  uploadProgress?: number
  errorMessage?: string
  onRetry?: () => void
}

export default function UploadPreviewModal({
  file,
  onConfirm,
  onClose,
  uploadState = 'idle',
  uploadProgress = 0,
  errorMessage,
  onRetry,
}: UploadPreviewModalProps) {
  const [previewUrl, setPreviewUrl] = useState('')
  const [filename, setFilename] = useState(file.name)
  const [alt, setAlt] = useState('')

  useEffect(() => {
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  const handleConfirm = () => {
    onConfirm({ filename: filename.trim() || file.name, alt: alt.trim() })
  }

  const isUploading = uploadState === 'uploading'
  const isSuccess = uploadState === 'success'
  const isError = uploadState === 'error'

  return (
    <Modal title="Upload Preview" onClose={isUploading ? () => {} : onClose}>
      <div className="space-y-4">
        {isSuccess ? (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <CheckCircle className="text-green-400" size={48} />
            <p className="text-white font-semibold">Upload berhasil!</p>
            <p className="text-zinc-400 text-sm">{filename.trim() || file.name}</p>
          </div>
        ) : (
          <>
            {previewUrl && (
              <img
                src={normalizeImageKitUrl(previewUrl)}
                alt={alt || file.name}
                className="w-full max-h-64 object-contain bg-zinc-950 rounded"
              />
            )}

            <div className="flex items-center gap-2">
              <span className="text-[10px] tracking-wide px-1.5 py-0.5 rounded bg-zinc-700 text-zinc-400 uppercase">
                {file.type.split('/')[1]}
              </span>
              <span className="text-zinc-500 text-xs">{formatSize(file.size)}</span>
            </div>

            <div>
              <label className="block text-xs text-zinc-400 mb-1">Filename</label>
              <input
                type="text"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                disabled={isUploading}
                className="w-full bg-zinc-800 text-white rounded px-3 py-2 text-sm border border-zinc-700 focus:outline-none focus:border-zinc-500 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-xs text-zinc-400 mb-1">Alt Text</label>
              <input
                type="text"
                value={alt}
                onChange={(e) => setAlt(e.target.value)}
                placeholder="Deskripsi gambar untuk aksesibilitas"
                disabled={isUploading}
                className="w-full bg-zinc-800 text-white rounded px-3 py-2 text-sm border border-zinc-700 focus:outline-none focus:border-zinc-500 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>

            {isUploading && (
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs text-zinc-400">
                  <span>Mengupload...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-zinc-700 rounded-full h-1.5">
                  <div
                    className="bg-yellow-500 h-1.5 rounded-full transition-all duration-150"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {isError && (
              <div className="flex items-start gap-2 bg-red-900/30 border border-red-800 rounded-lg px-3 py-2">
                <XCircle size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-300 flex-1">{errorMessage ?? 'Upload gagal, coba lagi.'}</p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                disabled={isUploading}
                className="text-sm text-zinc-400 hover:text-white transition-colors px-4 py-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              {isError ? (
                <button
                  type="button"
                  onClick={onRetry}
                  className="bg-yellow-500 hover:bg-yellow-400 text-black text-sm font-semibold px-6 py-2 rounded transition-colors"
                >
                  Coba Lagi
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={isUploading}
                  className="bg-yellow-500 hover:bg-yellow-400 disabled:opacity-40 disabled:cursor-not-allowed text-black text-sm font-semibold px-6 py-2 rounded transition-colors flex items-center gap-2"
                >
                  {isUploading ? (
                    <>
                      <span className="inline-block w-3 h-3 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      Uploading...
                    </>
                  ) : 'Upload'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
